// Yönetim işlem geçmişi sayfasının saf yardımcıları.
// Sayfa bileşeni yalnızca çizimden sorumlu kalsın diye gruplama, tarih
// kısayolları ve filtre özetleri buraya toplandı.

const locale = 'tr-TR';

// Etkilenen kayıt türlerinin okunabilir karşılığı. Filtre listesi ve aktif
// filtre çipleri aynı sözlüğü okur; iki yerde ayrı yazılırsa panel aynı kayda
// iki farklı ad verir.
export const TARGET_LABELS = {
  user: 'Kullanıcı',
  transaction: 'Finans talebi',
  kyc_request: 'KYC başvurusu',
  settings: 'Site ayarları',
  game_source: 'Canlı casino servisi',
  game_service: 'Oyun servisi',
  payment_method: 'Ödeme yöntemi',
  withdrawal_crypto_option: 'Kripto çekim seçeneği',
  slider: 'Banner',
  bonus: 'Bonus kampanyası',
  promo_code: 'Promosyon kodu',
  callback: 'Arama talebi',
  affiliate: 'Affiliate ortağı',
  affiliate_withdraw: 'Affiliate çekim talebi',
  footer_page: 'Footer sayfası',
  inbox_message: 'Üye mesajı',
  mail_log: 'Toplu e-posta',
};

export const targetLabel = (value) => (
  TARGET_LABELS[value] || 'Diğer yönetim kaydı'
);

export const EMPTY_FILTERS = {
  q: '',
  admin_id: '',
  role: '',
  section: '',
  action: '',
  target_type: '',
  date_from: '',
  date_to: '',
};

// Gelişmiş panel kapalıyken de aktif kalabilen filtreler; kapalı panelde
// gizli bir filtrenin sonucu daralttığı çiplerden anlaşılır.
export const ADVANCED_KEYS = [
  'admin_id', 'role', 'section', 'action', 'target_type',
];

const pad = (value) => String(value).padStart(2, '0');

const toDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

// Tarih girdileri <input type="date"> biçimindedir (YYYY-AA-GG); yerel gün
// sınırı kaymasın diye ISO metni elle kurulur.
export const isoDate = (date) => (
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
);

export const formatTime = (value, fallback = '') => {
  const date = toDate(value);
  if (!date) return fallback;
  return date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
};

export const formatDayTitle = (value, fallback = 'Tarihi bilinmeyen kayıtlar') => {
  const date = toDate(value);
  if (!date) return fallback;
  return date.toLocaleDateString(locale, {
    day: 'numeric', month: 'long', year: 'numeric', weekday: 'long',
  });
};

/**
 * Gün başlığının göreli kısmı: bugün ve dün tarihten daha hızlı okunur.
 * ``now`` testlerin sabit bir güne yaslanabilmesi için dışarıdan verilir.
 */
export const relativeDayLabel = (value, now = new Date()) => {
  const date = toDate(value);
  if (!date) return '';
  const today = isoDate(now);
  const yesterday = new Date(now.getTime());
  yesterday.setDate(yesterday.getDate() - 1);
  if (isoDate(date) === today) return 'Bugün';
  if (isoDate(date) === isoDate(yesterday)) return 'Dün';
  return '';
};

/**
 * Kayıtları gününe göre öbekler. Liste backend'den tarihe göre azalan gelir;
 * sıra korunur, yalnızca gün başlıkları araya girer.
 */
export const groupByDay = (items = [], now = new Date()) => {
  const groups = [];
  const index = new Map();
  items.forEach((item) => {
    const date = toDate(item?.created_at);
    const key = date ? isoDate(date) : 'unknown';
    if (!index.has(key)) {
      const group = {
        key,
        title: formatDayTitle(item?.created_at),
        relative: relativeDayLabel(item?.created_at, now),
        items: [],
      };
      index.set(key, group);
      groups.push(group);
    }
    index.get(key).items.push(item);
  });
  return groups;
};

/**
 * Hızlı tarih aralıkları. ``all`` iki alanı da boşaltır, yani filtre kalkar.
 */
export const DATE_PRESETS = [
  { id: 'all', label: 'Tümü', days: 0 },
  { id: 'today', label: 'Bugün', days: 1 },
  { id: 'week', label: 'Son 7 gün', days: 7 },
  { id: 'month', label: 'Son 30 gün', days: 30 },
];

export const presetRange = (id, now = new Date()) => {
  const preset = DATE_PRESETS.find((item) => item.id === id);
  if (!preset || preset.days === 0) return { date_from: '', date_to: '' };
  const start = new Date(now.getTime());
  start.setDate(start.getDate() - (preset.days - 1));
  return { date_from: isoDate(start), date_to: isoDate(now) };
};

/** Seçili aralık hangi kısayola denk düşüyor; hiçbiri değilse boş döner. */
export const activePreset = (filters, now = new Date()) => {
  const match = DATE_PRESETS.find((preset) => {
    const range = presetRange(preset.id, now);
    return range.date_from === (filters.date_from || '')
      && range.date_to === (filters.date_to || '');
  });
  return match ? match.id : '';
};

const shortDate = (value) => {
  const date = toDate(value);
  return date ? date.toLocaleDateString(locale) : String(value || '');
};

/**
 * Uygulanmış filtrelerin çip listesi. Değerler kimlik taşıdığı için
 * (yetkili kimliği, bölüm kodu) okunabilir karşılıkları facet listelerinden
 * çözülür; çözülemezse ham değer gösterilir, çip yine de kaldırılabilir.
 */
export const filterChips = (filters, facets = {}) => {
  const chips = [];
  const push = (key, label, value) => {
    if (value) chips.push({ key, label, value });
  };
  push('q', 'Arama', filters.q);
  const actor = (facets.actors || []).find(
    (item) => item.id === filters.admin_id);
  push(
    'admin_id', 'Yetkili',
    filters.admin_id ? (actor?.username || filters.admin_id) : '',
  );
  const role = (facets.roles || []).find((item) => item.id === filters.role);
  push('role', 'Rol', filters.role ? (role?.label || filters.role) : '');
  const section = (facets.sections || []).find(
    (item) => item.id === filters.section);
  push(
    'section', 'Bölüm',
    filters.section ? (section?.label || filters.section) : '',
  );
  const action = (facets.actions || []).find(
    (item) => item.id === filters.action);
  push(
    'action', 'İşlem',
    filters.action ? (action?.label || filters.action) : '',
  );
  push(
    'target_type', 'Kayıt türü',
    filters.target_type ? targetLabel(filters.target_type) : '',
  );
  push('date_from', 'Başlangıç', filters.date_from ? shortDate(filters.date_from) : '');
  push('date_to', 'Bitiş', filters.date_to ? shortDate(filters.date_to) : '');
  return chips;
};

/** Kayıt kartının sol tarafındaki renk şeridi; backend'in severity'si sürer. */
export const severityTone = (severity) => ({
  success: 'is-success',
  danger: 'is-danger',
  warning: 'is-warning',
  info: 'is-info',
}[severity] || 'is-info');

export const severityBadge = (severity) => ({
  success: 'badge-green',
  danger: 'badge-red',
  warning: 'badge-yellow',
  info: 'badge-blue',
}[severity] || 'badge-blue');

/** Avatar baş harfi; Türkçe büyütme kuralıyla (ı → I değil İ olmasın diye). */
export const actorInitial = (username) => (
  String(username || '?').trim().slice(0, 1).toLocaleUpperCase(locale) || '?'
);
